type DeepMergeable = Record<string | number | symbol, any>;

/**
 * Checks if a value is a plain object (not an array, Date, Map, Set, etc.)
 */
function isPlainObject(value: unknown): value is DeepMergeable {
  return (
    typeof value === "object" &&
    value !== null &&
    value.constructor === Object &&
    Object.prototype.toString.call(value) === "[object Object]"
  );
}

/**
 * Deeply merges two objects, with the second object taking precedence.
 * Preserves Map, Set, Date, and other special objects without merging them.
 * Arrays are replaced entirely (not merged element-wise).
 * Includes cycle detection to prevent infinite loops.
 *
 * @param target - The target object to merge into
 * @param source - The source object to merge from
 * @param visited - Internal parameter for cycle detection
 * @returns A new object with merged properties
 */
function deepMerge<T extends DeepMergeable, U extends DeepMergeable>(
  target: T,
  source: U,
  visited: WeakSet<object> = new WeakSet(),
): T & U {
  // Handle null/undefined cases
  if (!target || !isPlainObject(target)) {
    return (source || {}) as T & U;
  }

  if (!source || !isPlainObject(source)) {
    return (target || {}) as T & U;
  }

  // Check for circular references
  if (visited.has(source) || visited.has(target)) {
    // Return a shallow copy to break the cycle
    return { ...target, ...source } as T & U;
  }

  // Add current objects to visited set
  visited.add(source);
  visited.add(target);

  // Create a new object to avoid mutating the original
  const result = { ...target } as any;

  // Get all enumerable string/number keys and symbol keys
  const sourceKeys = [
    ...Object.keys(source),
    ...Object.getOwnPropertySymbols(source),
  ];

  // Iterate through all source properties (including symbols)
  for (const key of sourceKeys) {
    if (Object.hasOwn(source, key)) {
      const sourceValue = source[key];
      const targetValue = result[key];

      // If both values are plain objects, merge them recursively
      if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
        result[key] = deepMerge(targetValue, sourceValue, visited);
      } else {
        // Otherwise, source value takes precedence
        // This handles primitives, arrays, Date, Map, Set, etc.
        result[key] = sourceValue;
      }
    }
  }

  // Remove from visited set when backtracking (optional cleanup)
  visited.delete(source);
  visited.delete(target);

  return result as T & U;
}

export { deepMerge };
